import React, { memo, useCallback, useEffect, useRef, useState } from 'react'
import { Select } from 'antd'
import type { CustomCellEditorProps } from 'ag-grid-react'
import { useGridCellEditor } from 'ag-grid-react'
import './SelectCellEditor.css'

interface SelectOption {
  key?: string
  value: string | number
  label: React.ReactNode
}

interface SelectCellEditorParams {
  options: SelectOption[]
  valueListMaxHeight?: number
  formatValue?: (value: string) => string
  allowClear?: boolean
}

/**
 * Custom Select Cell Editor with Ant Design styling
 *
 * Provides a styled dropdown select editor for AG Grid cells.
 * Supports both cell editing and fullRow editing via focusIn/focusOut.
 */
const SelectCellEditor = memo(
  ({ value, onValueChange, stopEditing, column, cellStartedEdit }: CustomCellEditorProps<any, string>) => {
    const selectRef = useRef<any>(null)
    const [isOpen, setIsOpen] = useState(false)

    // Get custom params from column definition
    const params = (column?.getColDef()?.cellEditorParams || {}) as SelectCellEditorParams
    const { options = [], valueListMaxHeight = 200 } = params

    // Auto-open dropdown and focus on mount only if this cell started the edit
    useEffect(() => {
      if (cellStartedEdit) {
        setTimeout(() => {
          selectRef.current?.focus()
          setIsOpen(true)
        }, 0)
      }
    }, [cellStartedEdit])

    const focusIn = useCallback(() => {
      setIsOpen(true)
      setTimeout(() => selectRef.current?.focus(), 0)
    }, [])

    const focusOut = useCallback(() => {
      setIsOpen(false)
    }, [])

    useGridCellEditor({ focusIn, focusOut })

    const handleChange = (newValue: string) => {
      onValueChange(newValue)
      if (cellStartedEdit) {
        setTimeout(() => {
          setIsOpen(false)
          stopEditing()
        }, 0)
      } else {
        setIsOpen(false)
      }
    }

    const handleKeyDown = (e: React.KeyboardEvent) => {
      // Cancel editing on Escape
      if (e.key === 'Escape') {
        e.stopPropagation()
        setIsOpen(false)
        stopEditing(true) // Cancel = true
      }
    }

    return (
      <Select
        ref={selectRef}
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        open={isOpen}
        onOpenChange={setIsOpen}
        options={options}
        className="select-cell-editor"
        style={{ width: '100%' }}
        listHeight={valueListMaxHeight}
        showSearch
        optionFilterProp="label"
        getPopupContainer={() => document.body}
      />
    )
  }
)

SelectCellEditor.displayName = 'SelectCellEditor'

export default SelectCellEditor
